/**
 * Best-effort wipe of every browser-side persistence layer the app
 * touches. The "Reset & reload" button in `<app-boot-error>` calls
 * this when boot is stalled — the most common stuck-loader cause we've
 * identified is a poisoned Amplify session in `localStorage` /
 * IndexedDB that survives cookie clears, so this utility exists to
 * give the user a single-click cure.
 *
 * Best-effort: each step is wrapped in its own try/catch so a single
 * browser-API failure (storage disabled, IndexedDB blocked, SW
 * unregister rejected) doesn't abort the rest. The caller chains
 * `__reloadWithFreshShell()` after the returned promise resolves; we
 * never throw from here.
 *
 * Why "Amplify signOut" is included and raced with a short timeout:
 * a broken Amplify session is the most likely reason this function is
 * being called, so the signOut call itself may hang — defeating the
 * whole point of the reset. The race keeps the reset fast.
 */

const AMPLIFY_SIGNOUT_TIMEOUT_MS = 2_000;

/**
 * Per-step ceiling on each non-Amplify cleanup step (IndexedDB delete,
 * Cache Storage purge, SW unregister). If any single browser API
 * hangs — a blocked IndexedDB with a connection from another tab, a
 * misbehaving SW that won't unregister — the step is abandoned so
 * the overall reset can still complete and `__reloadWithFreshShell()`
 * can fire. 3 s is generous for these APIs in normal conditions;
 * anything longer is hung.
 */
const STEP_TIMEOUT_MS = 3_000;

/**
 * Race a promise against a timeout. Resolves to `undefined` on
 * timeout — callers using this for cleanup don't care WHY a step
 * didn't complete, only that the orchestrator can move on.
 */
function withStepTimeout(p: Promise<unknown>): Promise<void> {
  return Promise.race([
    p.then(
      () => undefined,
      () => undefined
    ),
    new Promise<void>(resolve => setTimeout(resolve, STEP_TIMEOUT_MS)),
  ]);
}

/**
 * IndexedDB names to delete when the browser doesn't support
 * `indexedDB.databases()` (Firefox < 126, all current Safari). Keep
 * synced with Amplify v6 internal storage:
 *   - `amplify-datastore` — DataStore (currently unused but Amplify
 *     v6 creates the DB on load in some configurations).
 *   - `amplify-datastore-storage-adapter-namespace` — same.
 *   - `aws-amplify-cache` — Cache module key-value store.
 *
 * Why: when Amplify's IndexedDB-backed token cache holds a
 * malformed entry, `fetchAuthSession()` can hang trying to
 * read/migrate it. Clearing these DBs is the cure for that class of
 * failure when the user clicks "Reset & reload".
 */
const KNOWN_INDEXEDDB_NAMES = [
  'amplify-datastore',
  'amplify-datastore-storage-adapter-namespace',
  'aws-amplify-cache',
];

export async function resetAppState(): Promise<void> {
  // Each step bounded — see STEP_TIMEOUT_MS / AMPLIFY_SIGNOUT_TIMEOUT_MS.
  // `Promise.allSettled` so a single step's hang or rejection doesn't
  // skip the rest; `withStepTimeout` so a single API's hang can't
  // hold the whole reset hostage.
  await Promise.allSettled([
    withStepTimeout(clearWebStorage()),
    withStepTimeout(clearIndexedDB()),
    withStepTimeout(clearCacheStorage()),
    withStepTimeout(unregisterServiceWorkers()),
    amplifySignOutWithTimeout(),
  ]);
}

function clearWebStorage(): Promise<void> {
  return new Promise(resolve => {
    try {
      localStorage.clear();
    } catch {
      // Storage disabled (Safari private mode, enterprise policy) —
      // nothing to clear and nothing to do.
    }
    try {
      sessionStorage.clear();
    } catch {
      // see above
    }
    resolve();
  });
}

async function clearIndexedDB(): Promise<void> {
  const names = await listIndexedDBNames();
  await Promise.allSettled(
    names.map(
      name =>
        new Promise<void>(resolve => {
          try {
            const req = indexedDB.deleteDatabase(name);
            req.onsuccess = () => resolve();
            req.onerror = () => resolve();
            req.onblocked = () => resolve();
          } catch {
            resolve();
          }
        })
    )
  );
}

async function listIndexedDBNames(): Promise<readonly string[]> {
  // `indexedDB.databases()` returns an array of `{name, version}`.
  // Not supported in Firefox < 126 or any current Safari; fall back
  // to the hard-coded list above for those.
  try {
    const dbs: { name?: string }[] =
      typeof indexedDB.databases === 'function' ? await indexedDB.databases() : [];
    const names = dbs.map(d => d.name).filter((n): n is string => !!n);
    // Merge with the known list so we still hit Amplify-internal DBs
    // even if `databases()` is implemented but omits them for some
    // reason (early Chromium behaviour we've seen in the wild).
    return Array.from(new Set([...names, ...KNOWN_INDEXEDDB_NAMES]));
  } catch {
    return KNOWN_INDEXEDDB_NAMES;
  }
}

async function clearCacheStorage(): Promise<void> {
  if (!('caches' in globalThis)) return;
  try {
    const keys = await caches.keys();
    await Promise.allSettled(keys.map(k => caches.delete(k)));
  } catch {
    // Cache Storage API unavailable.
  }
}

async function unregisterServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.allSettled(regs.map(r => r.unregister()));
  } catch {
    // ServiceWorker API unavailable or rejected.
  }
}

/**
 * Amplify signOut may itself hang on a broken session (the same
 * symptom that motivated this whole utility). Race it against a 2 s
 * timeout so the reset isn't held hostage by exactly the failure mode
 * it's trying to cure. Dynamic-imported so this utility doesn't pull
 * Amplify into the bundle when used from non-auth code (it currently
 * is only used by `<app-boot-error>` which already loads Amplify via
 * the auth path, but dynamic-import keeps the dependency direction
 * one-way).
 */
async function amplifySignOutWithTimeout(): Promise<void> {
  try {
    const { signOut } = await import('aws-amplify/auth');
    await Promise.race([
      signOut({ global: false }),
      new Promise<void>(resolve => setTimeout(resolve, AMPLIFY_SIGNOUT_TIMEOUT_MS)),
    ]);
  } catch {
    // Amplify never configured, signOut threw synchronously, or the
    // race timed out — all best-effort. The localStorage/IndexedDB
    // wipes above are the primary recovery; Cognito server-side
    // session will expire naturally if signOut didn't actually reach
    // the server.
  }
}
